import { PATHS } from 'router/config';

/**
 * ТЗ 0104, §5 — вкладки страницы «ИИ-агент» (`/ai-agent`). Список живёт в
 * `core`, а не на странице: его читает разбор адреса `useAiAgentsDeepLink`,
 * а слой `core` не может импортировать страницу. Порядок — порядок полосы
 * вкладок.
 */
export const AI_AGENTS_TABS = [
  'overview',
  'runs',
  'chat',
  'knowledge',
  'tools',
  'quality',
  'settings',
] as const;

export type AiAgentsTabId = typeof AI_AGENTS_TABS[number];

/** Вкладка по умолчанию в адрес не пишется: `/ai-agent` — это «Обзор». */
export const AI_AGENTS_DEFAULT_TAB: AiAgentsTabId = 'overview';

export const isAiAgentsTab = (
  value: string | null | undefined,
): value is AiAgentsTabId =>
  Boolean(value) &&
  (AI_AGENTS_TABS as readonly string[]).includes(value as string);

/** Имя параметра вкладки. Параметры вкладок не могут его занять. */
export const AI_AGENTS_TAB_PARAM = 'tab';

/**
 * Параметры вкладки в адресе: `?tab=runs&run=12&agent=slack`. `null`,
 * `undefined` и пустая строка — «снять параметр».
 */
export type AiAgentsUrlParams = Readonly<
  Record<string, string | null | undefined>
>;

/** Вкладка из адреса; незнакомая — «Обзор»: ссылки живут дольше вкладок. */
export const parseAiAgentsTab = (search: string): AiAgentsTabId => {
  const raw = new URLSearchParams(search).get(AI_AGENTS_TAB_PARAM);
  return isAiAgentsTab(raw) ? raw : AI_AGENTS_DEFAULT_TAB;
};

/** Параметр вкладки из адреса (обрезанный); нет или пустой — `null`. */
export const readAiAgentsParam = (
  search: string,
  name: string,
): string | null => {
  if (name === AI_AGENTS_TAB_PARAM) return null;
  const value = (new URLSearchParams(search).get(name) ?? '').trim();
  return value || null;
};

const toSearch = (params: URLSearchParams): string => {
  const text = params.toString();
  return text ? `?${text}` : '';
};

const applyParams = (
  target: URLSearchParams,
  patch: AiAgentsUrlParams | undefined,
): void => {
  Object.entries(patch ?? {}).forEach(([name, value]) => {
    if (name === AI_AGENTS_TAB_PARAM) return;
    const text = (value ?? '').trim();
    if (text) target.set(name, text);
    else target.delete(name);
  });
};

/**
 * Адрес вкладки с ЧИСТЫМИ параметрами: параметры прежней вкладки к новой не
 * относятся (выбранный прогон на вкладке чата — бессмыслица).
 */
export const buildAiAgentsSearch = (
  tab: AiAgentsTabId,
  params?: AiAgentsUrlParams,
): string => {
  const out = new URLSearchParams();
  if (tab !== AI_AGENTS_DEFAULT_TAB) out.set(AI_AGENTS_TAB_PARAM, tab);
  applyParams(out, params);
  return toSearch(out);
};

/** Поправить параметры текущей вкладки, не трогая остальные и саму вкладку. */
export const patchAiAgentsSearch = (
  search: string,
  patch: AiAgentsUrlParams,
): string => {
  const out = new URLSearchParams(search);
  applyParams(out, patch);
  return toSearch(out);
};

/**
 * Ссылка на вкладку страницы — для блоков вне неё (задача из Slack → прогон,
 * настройки ИИ → обзор). Пути — только отсюда, не шаблонной строкой.
 */
export const aiAgentsHref = (
  tab: AiAgentsTabId = AI_AGENTS_DEFAULT_TAB,
  params?: AiAgentsUrlParams,
): string => `${PATHS.aiAgents}${buildAiAgentsSearch(tab, params)}`;
